import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bookmark, Trash2, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import AILabShell from "@/components/ai-lab/AILabShell";
import CopyButton from "@/components/ai-lab/CopyButton";
import { CATEGORY_BY_ID, ACCENT_CLASSES } from "@/data/ai-lab/categories";
import { savedPromptsApi } from "@/lib/ai-lab/api";
import { cn } from "@/lib/utils";

/**
 * SavedPromptsPage — route: /ai-cpg-tools/saved
 * Prompts the user saved from the Prompt Builder (stored per user in MongoDB).
 */
export default function SavedPromptsPage() {
  const [items, setItems] = useState(null);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    savedPromptsApi.list().then(setItems).catch(() => setItems([]));
  }, []);

  const remove = async (id) => {
    try {
      await savedPromptsApi.remove(id);
      setItems((list) => list.filter((i) => i.id !== id));
      toast.success("Removed.");
    } catch {
      toast.error("Couldn't remove right now.");
    }
  };

  return (
    <AILabShell crumbs={[{ label: "My Prompts" }]} title="My Prompts" subtitle="Everything you've saved from the Prompt Builder, ready to copy again or rebuild with new answers.">
      {items === null && <p className="text-sm text-muted-foreground">Loading…</p>}
      {items && items.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center gap-3 p-10 text-center">
            <Bookmark className="h-8 w-8 text-muted-foreground" />
            <p className="font-medium">No saved prompts yet</p>
            <p className="max-w-sm text-sm text-muted-foreground">Generate a prompt and click <strong>Save</strong> to keep it here.</p>
            <Button asChild><Link to="/ai-cpg-tools/prompt-builder"><Wand2 className="mr-2 h-4 w-4" /> Open Prompt Builder</Link></Button>
          </CardContent>
        </Card>
      )}
      <div className="space-y-3">
        {items?.map((item) => {
          const cat = CATEGORY_BY_ID[item.category];
          const open = openId === item.id;
          return (
            <Card key={item.id}>
              <CardContent className="p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <div className="mb-1 flex items-center gap-2">
                      {cat && <Badge variant="outline" className={cn("border-transparent font-medium", ACCENT_CLASSES[cat.accent]?.badge)}>{cat.short}</Badge>}
                      <span className="text-xs text-muted-foreground">{item.createdAt ? new Date(item.createdAt).toLocaleDateString() : ""}</span>
                    </div>
                    <h3 className="font-semibold">{item.title}</h3>
                    {!open && <p className="line-clamp-2 text-sm text-muted-foreground">{item.prompt}</p>}
                  </div>
                  <div className="flex shrink-0 flex-wrap gap-2">
                    <CopyButton text={item.prompt} size="sm" />
                    <Button asChild size="sm" variant="outline"><Link to={`/ai-cpg-tools/prompt-builder/${item.taskId}`}>Rebuild</Link></Button>
                    <Button size="sm" variant="ghost" onClick={() => setOpenId(open ? null : item.id)}>{open ? "Hide" : "View"}</Button>
                    <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => remove(item.id)} aria-label="Delete"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
                {open && <pre className="mt-3 max-h-96 overflow-auto whitespace-pre-wrap rounded-md bg-muted/50 p-3 font-mono text-[12px] leading-relaxed">{item.prompt}</pre>}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </AILabShell>
  );
}
